/* eslint-disable @typescript-eslint/member-ordering */
/// <reference types="@types/google.maps" />
import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { AddressesApiService } from '@rma/addresses/public/api-addresses';
import { nonNullable } from '@rma/common-ui-rxjs/operators';
import { AutoCompleteResult, AutoSearchApiService, LocationsAutoSearchResult } from '@rma/generic/api/auto-search';
import { LocationType, NewAddressDetails, PagedList } from '@rma/generic/domain/types';
import { GoogleMapsAutoCompleteSuggestionService } from '@rma/maps/api';
import { SearchType } from '@rma/search/domain';
import { catchError, EMPTY, filter, map, Observable, switchMap } from 'rxjs';

export interface AddressSearch {
  searchType: SearchType;
  searchTerm: string;
  skip: number;
  results: PagedList<AutoCompleteResult>;
  selectedAddress?: { addressId?: number; address?: string; locationId: string; localityName: string; locationType: LocationType };
  error?: unknown;
}

@Injectable()
export class AddressSearchStore extends ComponentStore<AddressSearch> {
  private readonly autoSearchApiService = inject(AutoSearchApiService);
  private readonly googleMapsAutoCompleteSuggestionService = inject(GoogleMapsAutoCompleteSuggestionService);
  private readonly addressesApiService = inject(AddressesApiService);

  readonly searchType$ = this.select(({ searchType }) => searchType);
  readonly searchTerm$ = this.select(({ searchTerm }) => searchTerm).pipe(nonNullable());
  readonly skip$ = this.select(({ skip }) => skip ?? 0);

  private readonly locationSearchRequest$ = this.select(
    { skip: this.skip$, searchTerm: this.searchTerm$, searchType: this.searchType$ },
    { debounce: true },
  ).pipe(filter(({ searchType }) => searchType === 'Locations'));

  private readonly addressSearchRequest$ = this.select(
    { searchTerm: this.searchTerm$, searchType: this.searchType$ },
    { debounce: true },
  ).pipe(filter(({ searchTerm, searchType }) => !!searchTerm && searchType === 'StreetAddress'));

  readonly locationSearchResults$ = this.select(({ results }) => results);
  readonly selectedAddress$ = this.select(({ selectedAddress }) => selectedAddress).pipe(nonNullable());

  constructor() {
    super({
      searchType: 'Locations',
      searchTerm: '',
      skip: 0,
      results: { skip: 0, take: 0, total: 0, results: [] },
    });

    this.locationSearchEffect(this.locationSearchRequest$);
    this.addressSearchEffect(this.addressSearchRequest$);
  }

  public readonly locationSearchEffect = this.effect((request$: Observable<{ skip: number; searchTerm: string }>) =>
    request$.pipe(
      switchMap(({ skip, searchTerm }) =>
        this.autoSearchApiService
          .getLocations({
            skip,
            take: 10,
            searchTerm,
          })
          .pipe(
            tapResponse(
              (results) => this.setLocationSearchResults({ skip, results }),
              (error) => {
                console.error(error);
                this.patchState({ error });
              },
            ),
          ),
      ),
    ),
  );

  public readonly locationSelected = this.updater(
    (state, selectedAddress: { addressId?: number; locationId: string; localityName: string; locationType: LocationType }) => ({
      ...state,
      selectedAddress,
    }),
  );

  public readonly addressSearchEffect = this.effect((request$: Observable<{ searchTerm: string }>) =>
    request$.pipe(
      switchMap(({ searchTerm }) =>
        this.googleMapsAutoCompleteSuggestionService.search(searchTerm).pipe(
          tapResponse(
            (results) => this.setAddressSearchResults(results),
            (error) => {
              console.error(error);
              this.patchState({ error });
            },
          ),
        ),
      ),
    ),
  );

  public readonly addressSelectedEffect = this.effect((request$: Observable<string>) =>
    request$.pipe(
      switchMap((address) =>
        this.googleMapsAutoCompleteSuggestionService.addressToGeocoder(address).pipe(
          filter((results) => !!results),
          nonNullable(),
          map((results) => this.googleMapsAutoCompleteSuggestionService.mapPredictionToType<NewAddressDetails>(results)),
          nonNullable(),
          catchError((error) => {
            console.error(error);
            this.patchState({ error });
            return EMPTY;
          }),
          switchMap((newAddress) =>
            this.addressesApiService.createAddress(newAddress).pipe(
              tapResponse(
                ({ addressId, localityId, locality, fullAddress }) =>
                  this.patchState({
                    selectedAddress: {
                      addressId,
                      address: fullAddress,
                      locationId: localityId.toString(),
                      localityName: locality,
                      locationType: 'Locality',
                    },
                  }),
                (error) => {
                  console.error(error);
                  this.patchState({ error });
                },
              ),
            ),
          ),
        ),
      ),
    ),
  );

  public readonly setSearchType = this.updater((state, searchType: SearchType) => ({
    ...state,
    searchType,
    skip: 0,
    error: undefined,
  }));

  public readonly locationSearch = this.updater((state, searchTerm: string) => ({
    ...state,
    searchTerm,
    skip: 0,
    error: undefined,
  }));

  public readonly locationViewMore = this.updater((state) => ({
    ...state,
    skip: (state.skip ?? 0) + 10,
  }));

  private readonly setLocationSearchResults = this.updater(
    (state, response: { skip: number; results: PagedList<LocationsAutoSearchResult> }) => ({
      ...state,
      skip: response.skip,
      results:
        response.skip === 0
          ? { ...response.results, results: response.results.results.map(this.mapAutoSearchToAutoCompleteResult) }
          : {
              ...response.results,
              results: [...state.results.results, ...response.results.results.map(this.mapAutoSearchToAutoCompleteResult)],
            },
    }),
  );

  private readonly setAddressSearchResults = this.updater((state, response: google.maps.places.PlacePrediction[]) => ({
    ...state,
    results: { skip: 0, take: response.length, total: response.length, results: response.map(this.mapGooglePlaceToAutoCompleteResult) },
  }));

  private mapAutoSearchToAutoCompleteResult(autoSearch: LocationsAutoSearchResult): AutoCompleteResult {
    const { id, name, rawName, url, autoSearchSubType } = autoSearch;
    const result: AutoCompleteResult = {
      id,
      name,
      rawName,
      url,
      autoSearchSubType,
    };

    return result;
  }

  private mapGooglePlaceToAutoCompleteResult(place: google.maps.places.PlacePrediction): AutoCompleteResult {
    const result: AutoCompleteResult = {
      id: place.placeId,
      name: place.text.text,
      rawName: place.text.text,
      url: '',
    };

    return result;
  }
}
